import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { ScrollArea } from './ui/scroll-area';
import { useAuth } from '../contexts/AuthContext';
import { 
  Bell, 
  Brain, 
  Clock, 
  AlertTriangle, 
  Info, 
  CheckCircle,
  TrendingUp,
  Users,
  FileText,
  X,
  Calendar,
  Shield
} from 'lucide-react';
import { apiClient, RegulatoryChange } from '../lib/api';

interface Alert {
  id: string;
  title: string;
  message: string;
  type: 'urgent' | 'info' | 'success' | 'warning';
  timestamp: string;
  department: string;
  read: boolean;
  documentId?: number;
}

interface AlertsPanelProps {
  onClose?: () => void;
}

export function AlertsPanel({ onClose }: AlertsPanelProps) {
  const { user } = useAuth();
  const [aiSummaryEnabled, setAiSummaryEnabled] = useState(true);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [regulatoryChanges, setRegulatoryChanges] = useState<RegulatoryChange[]>([]);

  // Broadcast unread count whenever alerts change so header badge stays in sync
  useEffect(() => {
    const unreadCount = alerts.filter(alert => !alert.read).length;
    if (user?.id) {
      localStorage.setItem(`infervia_last_unread_count_${user.id}`, JSON.stringify(unreadCount));
    }
    window.dispatchEvent(
      new CustomEvent('alerts:updated', { detail: { unreadCount, userId: user?.id } })
    );
  }, [alerts, user?.id]);

  // Load alerts from API on mount
  useEffect(() => {
    if (user?.id) {
      loadAlerts();
    }
  }, [user?.id]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      console.log('🔔 Loading alerts from API...');

      // Fetch regulatory changes
      const changes = await apiClient.getRecentChanges(100);
      setRegulatoryChanges(changes);

      // Generate alerts from the data
      const generatedAlerts = generateAlertsFromData(changes);
      
      // Load read status from localStorage
      const readAlerts = getReadAlerts(user?.id);
      const alertsWithReadStatus = generatedAlerts.map(alert => ({
        ...alert,
        read: readAlerts.includes(alert.id)
      }));

      setAlerts(alertsWithReadStatus);
      console.log(`✅ Generated ${alertsWithReadStatus.length} alerts`);
    } catch (error) {
      console.error('❌ Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate alerts from regulatory changes data
  const generateAlertsFromData = (changes: RegulatoryChange[]): Alert[] => {
    const alerts: Alert[] = [];
    const now = new Date();

    // 1. NEW CHANGES ALERTS (last 48 hours)
    const recentChanges = changes.filter(change => {
      const changeDate = new Date(change.analysis_timestamp || change.discovered_date);
      const hoursSince = (now.getTime() - changeDate.getTime()) / (1000 * 60 * 60);
      return hoursSince <= 48;
    });

    if (recentChanges.length > 0) {
      // Group by department
      const byDepartment: Record<string, RegulatoryChange[]> = {};
      recentChanges.forEach(change => {
        change.affected_departments.forEach(dept => {
          if (!byDepartment[dept]) byDepartment[dept] = [];
          byDepartment[dept].push(change);
        });
      });

      // Create alert for each department with new changes
      Object.entries(byDepartment).forEach(([dept, deptChanges]) => {
        if (deptChanges.length > 0) {
          const latestChange = deptChanges[0];
          const changeDate = new Date(latestChange.analysis_timestamp || latestChange.discovered_date);
          // Deterministic ID so read state persists across reloads/user switches
          const latestDocId = latestChange.document_id;
          
          alerts.push({
            id: `new-${dept}-${latestDocId}`,
            title: 'New Regulatory Changes Detected',
            message: `${deptChanges.length} new ${dept.toLowerCase()}-related document${deptChanges.length > 1 ? 's' : ''} analyzed`,
            type: 'info',
            timestamp: getRelativeTime(changeDate),
            department: dept,
            read: false,
            documentId: latestChange.document_id
          });
        }
      });
    }

    // 2. URGENT DEADLINE ALERTS (within 7 days)
    const urgentDeadlines = changes.filter(change => {
      if (!change.simplified_analysis.compliance_deadline) return false;
      
      const deadline = new Date(change.simplified_analysis.compliance_deadline);
      const daysUntil = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      return daysUntil > 0 && daysUntil <= 7;
    });

    urgentDeadlines.forEach(change => {
      const deadline = new Date(change.simplified_analysis.compliance_deadline!);
      const daysUntil = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      alerts.push({
        id: `deadline-urgent-${change.document_id}`,
        title: 'Urgent Compliance Deadline',
        message: `${change.title.substring(0, 60)}... - Deadline in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`,
        type: 'urgent',
        timestamp: `${daysUntil} day${daysUntil !== 1 ? 's' : ''} remaining`,
        department: change.affected_departments[0] || 'Compliance',
        read: false,
        documentId: change.document_id
      });
    });

    // 3. UPCOMING DEADLINE ALERTS (8-30 days)
    const upcomingDeadlines = changes.filter(change => {
      if (!change.simplified_analysis.compliance_deadline) return false;
      
      const deadline = new Date(change.simplified_analysis.compliance_deadline);
      const daysUntil = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      return daysUntil > 7 && daysUntil <= 30;
    });

    if (upcomingDeadlines.length > 0) {
      upcomingDeadlines.slice(0, 3).forEach(change => {
        const deadline = new Date(change.simplified_analysis.compliance_deadline!);
        const daysUntil = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        alerts.push({
          id: `deadline-upcoming-${change.document_id}`,
          title: 'Upcoming Compliance Deadline',
          message: `${change.title.substring(0, 60)}...`,
          type: 'warning',
          timestamp: `${daysUntil} days remaining`,
          department: change.affected_departments[0] || 'Compliance',
          read: false,
          documentId: change.document_id
        });
      });
    }

    // 4. HIGH PRIORITY ALERTS
    const highPriorityChanges = changes.filter(change => {
      // High priority if: has next steps with high priority OR quality score is very high
      const hasHighPrioritySteps = change.simplified_analysis.next_steps.some(
        step => step.priority?.toLowerCase() === 'high'
      );
      const hasHighQualityScore = change.quality_control && change.quality_control.quality_score >= 9;
      
      return hasHighPrioritySteps || hasHighQualityScore;
    }).slice(0, 3);

    highPriorityChanges.forEach(change => {
      const changeDate = new Date(change.analysis_timestamp || change.discovered_date);
      
      alerts.push({
        id: `high-priority-${change.document_id}`,
        title: 'High Priority Regulatory Change',
        message: `${change.title.substring(0, 60)}... - Immediate attention recommended`,
        type: 'urgent',
        timestamp: getRelativeTime(changeDate),
        department: change.affected_departments[0] || 'Multiple',
        read: false,
        documentId: change.document_id
      });
    });

    // 5. QUALITY-VERIFIED DOCUMENTS
    const qualityVerified = changes.filter(change => 
      change.quality_control && 
      change.quality_control.safe_to_use && 
      change.quality_control.quality_score >= 8
    ).slice(0, 2);

    qualityVerified.forEach(change => {
      const changeDate = new Date(change.analysis_timestamp || change.discovered_date);
      
      alerts.push({
        id: `quality-${change.document_id}`,
        title: 'Quality Verification Complete',
        message: `${change.title.substring(0, 60)}... - High confidence analysis (${change.quality_control?.quality_score}/10)`,
        type: 'success',
        timestamp: getRelativeTime(changeDate),
        department: change.affected_departments[0] || 'Multiple',
        read: false,
        documentId: change.document_id
      });
    });

    // Sort alerts by priority: urgent > warning > info > success
    const priorityOrder = { urgent: 0, warning: 1, info: 2, success: 3 };
    alerts.sort((a, b) => priorityOrder[a.type] - priorityOrder[b.type]);

    return alerts;
  };

  // Get relative time string
  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
  };

  // LocalStorage helpers for read status
  const getReadAlerts = (userId?: string): string[] => {
    try {
      const key = userId ? `infervia_read_alerts_${userId}` : 'infervia_read_alerts';
      const stored = localStorage.getItem(key);

      // Fallback to legacy key so existing users keep their state
      if (!stored && userId) {
        const legacy = localStorage.getItem('infervia_read_alerts');
        return legacy ? JSON.parse(legacy) : [];
      }

      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const markAlertAsRead = (alertId: string) => {
    const readAlerts = getReadAlerts(user?.id);
    if (!readAlerts.includes(alertId)) {
      readAlerts.push(alertId);
      const key = user?.id ? `infervia_read_alerts_${user.id}` : 'infervia_read_alerts';
      localStorage.setItem(key, JSON.stringify(readAlerts));
      
      // Update state
      setAlerts(prev => prev.map(alert => 
        alert.id === alertId ? { ...alert, read: true } : alert
      ));
    }
  };

  const markAllAsRead = () => {
    const allAlertIds = alerts.map(a => a.id);
    const key = user?.id ? `infervia_read_alerts_${user.id}` : 'infervia_read_alerts';
    localStorage.setItem(key, JSON.stringify(allAlertIds));
    setAlerts(prev => prev.map(alert => ({ ...alert, read: true })));
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'urgent': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'warning': return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'info': return <Info className="w-5 h-5 text-blue-600" />;
      default: return <Bell className="w-5 h-5" />;
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'urgent': return 'border-l-red-500 bg-red-50';
      case 'warning': return 'border-l-yellow-500 bg-yellow-50';
      case 'success': return 'border-l-green-500 bg-green-50';
      case 'info': return 'border-l-blue-500 bg-blue-50';
      default: return 'border-l-gray-500 bg-gray-50';
    }
  };

  const unreadCount = alerts.filter(alert => !alert.read).length;

  // Generate AI Summary from real data
  const generateAISummary = () => {
    const recentCount = alerts.filter(a => a.type === 'info' || a.type === 'success').length;
    const urgentCount = alerts.filter(a => a.type === 'urgent').length;
    const warningCount = alerts.filter(a => a.type === 'warning').length;
    
    const totalChanges = regulatoryChanges.length;
    const departments = Array.from(new Set(regulatoryChanges.flatMap(c => c.affected_departments)));

    return `${totalChanges} regulatory changes detected across ${departments.length} departments. ${urgentCount} urgent item${urgentCount !== 1 ? 's' : ''} require${urgentCount === 1 ? 's' : ''} immediate attention. ${warningCount} upcoming deadline${warningCount !== 1 ? 's' : ''}. Source documents available for professional review.`;
  };

  if (loading) {
    return (
      <div className="w-96 bg-white border-l border-gray-200 h-full flex flex-col shadow-xl">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading alerts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-96 bg-white border-l border-gray-200 h-full flex flex-col shadow-xl">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Bell className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Alerts</h2>
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white">
                {unreadCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={markAllAsRead}
                className="text-xs"
              >
                Mark all read
              </Button>
            )}
            {onClose && (
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>

        {/* AI Summary Toggle */}
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center space-x-2">
            <Brain className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium">AI Summary</span>
          </div>
          <Switch 
            checked={aiSummaryEnabled} 
            onCheckedChange={setAiSummaryEnabled}
          />
        </div>
      </div>

      {/* AI Summary Preview */}
      {aiSummaryEnabled && (
        <div className="p-4 bg-blue-50 border-b border-blue-200">
          <h3 className="text-sm font-semibold mb-2 flex items-center">
            <Shield className="w-4 h-4 mr-1 text-blue-600" />
            Today's Information Summary
          </h3>
          <p className="text-sm text-gray-700">
            {generateAISummary()}
          </p>
        </div>
      )}

      {/* Alerts List */}
      <ScrollArea className="flex-1 bg-white">
        <div className="p-4 space-y-3">
          {alerts.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600">No new alerts</p>
              <p className="text-sm text-gray-500 mt-1">You're all caught up!</p>
            </div>
          ) : (
            alerts.map((alert) => (
              <div
                key={alert.id}
                onClick={() => markAlertAsRead(alert.id)}
                className={`p-4 border-l-4 rounded-r-lg cursor-pointer transition-opacity ${getAlertColor(alert.type)} ${
                  !alert.read ? 'shadow-sm' : 'opacity-60'
                }`}
              >
                <div className="flex items-start space-x-3">
                  {getAlertIcon(alert.type)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <h4 className="text-sm font-semibold mb-1">{alert.title}</h4>
                      {!alert.read && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1"></div>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{alert.message}</p>
                    <div className="flex items-center justify-between text-xs">
                      <Badge variant="outline" className="bg-white">
                        {alert.department}
                      </Badge>
                      <span className="text-gray-500">{alert.timestamp}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Professional Review Notice */}
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <div className="text-xs text-gray-600">
          <p className="font-semibold mb-2 flex items-center">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Professional Review Recommended
          </p>
          <p>
            Infervia provides regulatory information for professional evaluation. 
            Healthcare organizations should consult qualified professionals for compliance decisions.
          </p>
        </div>
      </div>
    </div>
  );
}
